import BuildUrl from "build-url";
import { defaultsDeep, isArray, isEmpty, isPlainObject } from "lodash";
import { isRef, reactive, ref, set, toRefs, unref, watch } from "vue-demi";

/**
 * @template T
 * @typedef {import("vue-demi").Ref<T>} Ref<T>
 */

/**
 * @typedef {import("@vueuse/core").MaybeRef} MaybeRef
 */

/**
 * @typedef {Object} QueryState
 * @property {boolean} loading
 * @property {boolean} finished
 * @property {boolean} success
 * @property {any} error
 * @property {any} data
 * @property {function} execute
 */

/**
 * @typedef {{[key: string]: QueryState}} QueryStateMap
 */

/**
 * @typedef {Object} UseFetchExParams
 * @property {RequestInit} reqInit
 * @property {boolean} immediate
 * @property {boolean} reactiveUrl
 * @property {any} defaultValue
 */

/**
 * @param {function} mutate
 * @param {string} fetchUrl
 * @param {object} fetchOptions
 */
function createQueryExecute(mutate, fetchUrl, fetchOptions) {
  return async (bodyParams = null, queryParams = null) => {
    if (
      !isEmpty(queryParams) &&
      (isPlainObject(queryParams) || isArray(queryParams))
    ) {
      fetchUrl = BuildUrl(fetchUrl, {
        queryParams
      });
    }

    if (
      !isEmpty(bodyParams) &&
      (isPlainObject(bodyParams) || isArray(bodyParams))
    ) {
      fetchOptions.body = JSON.stringify(bodyParams);
    }

    try {
      mutate("error", null);
      mutate("finished", false);
      mutate("loading", true);
      mutate("success", false);

      const fetchData = await fetch(fetchUrl, fetchOptions).then(r => r.json());

      mutate("data", fetchData);
      mutate("success", true);
    } catch (e) {
      mutate("error", e);
    } finally {
      mutate("loading", false);
      mutate("finished", true);
    }
  };
}

/**
 * @param {MaybeRef<string>} url
 * @param {UseFetchExParams} params
 */
export function useFetchEx(url, params) {
  params = defaultsDeep(params, {
    reqInit: {},
    immediate: true,
    reactiveUrl: true,
    defaultValue: null
  });

  /**
   * @type {QueryState}
   */
  const defaultQuery = reactive({
    data: null,
    error: false,
    finished: false,
    loading: false,
    success: false,
    execute: null
  });

  /**
   * @type {QueryStateMap}
   */
  const queries = reactive({});

  const urlRef = isRef(url) ? url : ref(url);

  const reqInitRef = isRef(params.reqInit)
    ? params.reqInit
    : ref(params.reqInit);

  const execute = async (
    bodyParams = null,
    queryParams = null,
    queryKey = null
  ) => {
    const fetchUrl = unref(urlRef);
    const fetchOptions = unref(reqInitRef);

    set(queries, queryKey, {
      data: null,
      error: false,
      finished: false,
      loading: false,
      success: false,
      execute: null
    });

    const mutator = (key, value) => {
      if (queryKey) {
        const state = queries[queryKey] || {};
        set(queries, queryKey, {
          ...state,
          [key]: value
        });
      } else {
        defaultQuery[key] = value;
      }
    };

    queries[queryKey].execute = createQueryExecute(
      mutator,
      fetchUrl,
      fetchOptions
    );

    return queries[queryKey].execute(bodyParams, queryParams);
  };

  const clear = () => {
    defaultQuery.data = null;
    defaultQuery.error = false;
    defaultQuery.finished = false;
    defaultQuery.loading = false;
    defaultQuery.success = false;
    defaultQuery.execute = null;
  };

  if (params.reactiveUrl) {
    watch(urlRef, () => execute());
  }

  if (params.immediate) {
    execute();
  }

  return {
    queries,
    ...toRefs(defaultQuery),
    execute,
    clear
  };
}
